import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import * as Sentry from '@sentry/react';

interface Organization {
  id: string;
  name: string;
  logo_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface OrganizationContextType {
  organization: Organization | null;
  loading: boolean;
  setOrganization: (org: Organization | null) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    // Try to get from localStorage
    const stored = localStorage.getItem('selectedOrganization');
    let org: Organization | null = null;
    if (stored) {
      try {
        org = JSON.parse(stored) as Organization;
      } catch (e) {
        // ignore JSON parse error
      }
    }
    // Only if not found in localStorage, use lastOrganizationId
    if (!org && user?.lastOrganizationId && user.organizations?.length) {
      const orgId = user.lastOrganizationId;
      // Try to get org name from cachedOrganizations
      const cached = localStorage.getItem('cachedOrganizations');
      let orgName = '';
      if (cached) {
        try {
          const orgs: { organization_id: string; organization_name: string }[] = JSON.parse(cached);
          const found = orgs.find((o) => o.organization_id === orgId);
          if (found) orgName = found.organization_name;
        } catch (e) {
          // ignore JSON parse error
        }
      }
      org = { id: orgId, name: orgName };
      // Update localStorage if needed
      localStorage.setItem('selectedOrganization', JSON.stringify(org));
    }
    setOrganization(org);

    // Set organization context in Sentry for troubleshooting
    if (org) {
      Sentry.setContext('organization', {
        id: org.id,
        name: org.name
      });
    }

    setLoading(false);
  }, [user, authLoading]);

  const handleSetOrganization = (org: Organization | null) => {
    setOrganization(org);
    if (org) {
      localStorage.setItem('selectedOrganization', JSON.stringify(org));
      // Set organization context in Sentry for troubleshooting
      Sentry.setContext('organization', {
        id: org.id,
        name: org.name
      });
    } else {
      localStorage.removeItem('selectedOrganization');
      // Clear organization context in Sentry
      Sentry.setContext('organization', null);
    }
  };

  return (
    <OrganizationContext.Provider value={{ organization, loading, setOrganization: handleSetOrganization }}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};